import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Users, Sparkles, Bookmark, Activity, Trash2, Crown, ArrowDownUp, Eye, ShieldAlert, Gauge } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="border border-border bg-white p-4" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${accent ? "text-[#111110]" : "text-muted-foreground"}`} aria-hidden="true" />
        <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
      </div>
      <p className="font-heading font-extrabold text-3xl">{value ?? "—"}</p>
    </div>
  );
}

function when(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function fmt(n) {
  if (n == null) return "—";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(2) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "k";
  return String(n);
}

export default function Admin() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewUser, setViewUser] = useState(null);
  const [viewSaved, setViewSaved] = useState([]);
  const [openArticle, setOpenArticle] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, u, a] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
        api.get("/admin/activity"),
      ]);
      setStats(s.data);
      setUsers(u.data.items);
      setActivity(a.data.items);
    } catch (e) {
      toast.error("Failed to load", { description: apiErr(e) });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (user && user.role !== "admin") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-4">
        <ShieldAlert className="w-10 h-10 text-[#B3232F]" aria-hidden="true" />
        <p className="font-mono text-sm">Admin access only.</p>
        <Button variant="outline" className="rounded-none" onClick={() => navigate("/app")}>Back to app</Button>
      </div>
    );
  }

  const togglePlan = async (u) => {
    const next = u.plan === "premium" ? "free" : "premium";
    try {
      await api.patch(`/admin/users/${u.user_id}/plan`, { plan: next });
      toast.success(`${u.email} → ${next}`);
      load();
    } catch (e) {
      toast.error("Update failed", { description: apiErr(e) });
    }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Delete ${u.email} and all their data? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error("Delete failed", { description: apiErr(e) });
    }
  };

  const openUserSaved = async (u) => {
    setViewUser(u);
    setViewSaved([]);
    setOpenArticle(null);
    try {
      const { data } = await api.get(`/admin/users/${u.user_id}/saved`);
      setViewSaved(data.items);
    } catch (e) {
      toast.error("Failed to load articles", { description: apiErr(e) });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="h-14 border-b border-border bg-white flex items-center justify-between px-5 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <Button data-testid="admin-back" variant="ghost" className="rounded-none font-mono text-xs" onClick={() => navigate("/app")}>
            <ArrowLeft className="w-4 h-4 mr-2" aria-hidden="true" /> Dashboard
          </Button>
          <span className="font-heading font-extrabold tracking-tight">Admin · Analytics</span>
        </div>
        <Button variant="outline" className="rounded-none font-mono text-xs" onClick={load}>Refresh</Button>
      </header>

      <div className="max-w-6xl mx-auto p-5">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 mb-6">
          <Stat icon={Users} label="Users" value={stats?.total_users} accent />
          <Stat icon={Crown} label="Premium" value={stats?.premium_users} />
          <Stat icon={Sparkles} label="Rewrites" value={stats?.total_rewrites} accent />
          <Stat icon={Sparkles} label="Today" value={stats?.rewrites_today} />
          <Stat icon={Bookmark} label="Saved" value={stats?.total_saved} />
          <Stat icon={Activity} label="Active Today" value={stats?.active_today} accent />
          <Stat icon={Gauge} label="Est Tokens" value={fmt(stats?.total_tokens)} accent />
        </div>

        <Tabs defaultValue="users">
          <TabsList className="rounded-none border-b border-border bg-transparent p-0 h-auto mb-4">
            <TabsTrigger data-testid="admin-tab-users" value="users" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-[#ffce00] font-mono text-xs uppercase">Users</TabsTrigger>
            <TabsTrigger data-testid="admin-tab-activity" value="activity" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-[#ffce00] font-mono text-xs uppercase">Activity</TabsTrigger>
          </TabsList>

          <TabsContent value="users" className="m-0">
            <div className="border border-border bg-white overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-secondary text-left">
                    {["User", "Plan", "Source", "Joined", "Rewrites (today/all)", "Saved", "Est. tokens", "Last active", ""].map((h) => (
                      <th key={h} className="px-3 py-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={9} className="px-3 py-6 text-center font-mono text-xs text-muted-foreground">Loading…</td></tr>
                  ) : users.map((u) => (
                    <tr key={u.user_id} className="border-b border-border hover:bg-secondary/50" data-testid={`admin-user-row`}>
                      <td className="px-3 py-2">
                        <p className="font-medium">{u.name || "—"}</p>
                        <p className="text-xs text-muted-foreground">{u.email}</p>
                      </td>
                      <td className="px-3 py-2">
                        <span className={`font-mono text-[10px] uppercase px-2 py-0.5 ${u.plan === "premium" ? "bg-[#111110] text-[#ffce00]" : "bg-secondary"}`}>{u.plan}</span>
                        {u.role === "admin" && <span className="ml-1 font-mono text-[10px] uppercase px-2 py-0.5 bg-[#ffce00] text-black">admin</span>}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{u.auth_provider}</td>
                      <td className="px-3 py-2 font-mono text-xs text-muted-foreground whitespace-nowrap">{when(u.created_at)}</td>
                      <td className="px-3 py-2 font-mono text-xs">{u.rewrites_today} / {u.rewrites_all_time}</td>
                      <td className="px-3 py-2 font-mono text-xs">{u.saved_count}</td>
                      <td className="px-3 py-2 font-mono text-xs">{fmt(u.tokens_total)}</td>
                      <td className="px-3 py-2 font-mono text-xs text-muted-foreground whitespace-nowrap">{when(u.last_active)}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1 justify-end">
                          <Button data-testid="admin-view-saved" size="sm" variant="ghost" className="rounded-none h-7 px-2" title="View saved articles" onClick={() => openUserSaved(u)}>
                            <Eye className="w-3.5 h-3.5" aria-hidden="true" />
                          </Button>
                          <Button data-testid="admin-toggle-plan" size="sm" variant="outline" className="rounded-none h-7 px-2" disabled={u.is_lifetime_free} title={u.is_lifetime_free ? "Lifetime free" : "Toggle plan"} onClick={() => togglePlan(u)}>
                            <ArrowDownUp className="w-3.5 h-3.5" aria-hidden="true" />
                          </Button>
                          <Button data-testid="admin-delete-user" size="sm" variant="outline" className="rounded-none h-7 px-2 text-[#B3232F] border-[#B3232F] disabled:opacity-30" disabled={u.role === "admin" || u.is_lifetime_free} title="Delete user" onClick={() => removeUser(u)}>
                            <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="activity" className="m-0">
            <div className="border border-border bg-white divide-y divide-border" data-testid="admin-activity-feed">
              {loading ? (
                <p className="px-4 py-6 text-center font-mono text-xs text-muted-foreground">Loading…</p>
              ) : activity.length === 0 ? (
                <p className="px-4 py-6 text-center font-mono text-xs text-muted-foreground">No activity yet. Rewrites will appear here.</p>
              ) : activity.map((a, i) => (
                <div key={i} className="px-4 py-3 flex items-start gap-3">
                  <Sparkles className="w-4 h-4 mt-0.5 text-[#111110]" aria-hidden="true" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm"><span className="font-semibold">{a.user_email || a.user_id}</span> rewrote <span className="text-muted-foreground">"{a.title}"</span></p>
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      <span className="font-mono text-[10px] uppercase bg-secondary px-1.5 py-0.5">{a.language}</span>
                      {a.authenticity != null && <span className={`font-mono text-[10px] px-1.5 py-0.5 ${a.authenticity < 90 ? "bg-[#FDECEC] text-[#B3232F]" : "bg-[#F4F7EE] text-[#3B5323]"}`}>Auth {a.authenticity}%</span>}
                      {a.eeat_overall != null && <span className="font-mono text-[10px] px-1.5 py-0.5 bg-[#111110] text-[#ffce00]">E-E-A-T {a.eeat_overall}%</span>}
                      {a.tokens_total != null && a.tokens_total > 0 && <span className="font-mono text-[10px] px-1.5 py-0.5 bg-secondary" title="Estimated tokens">~{fmt(a.tokens_total)} tok</span>}
                      {a.provider && <span className="font-mono text-[10px] px-1.5 py-0.5 bg-secondary text-muted-foreground" title="LLM provider used">{a.provider.startsWith("user-key") ? "own key" : "emergent"}</span>}
                      <span className="font-mono text-[10px] text-muted-foreground">{when(a.created_at)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* User saved articles dialog */}
      <Dialog open={!!viewUser} onOpenChange={(o) => { if (!o) { setViewUser(null); setOpenArticle(null); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto rounded-none" data-testid="admin-saved-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{viewUser?.email} · saved articles ({viewSaved.length})</DialogTitle>
          </DialogHeader>
          {viewSaved.length === 0 ? (
            <p className="font-mono text-xs text-muted-foreground py-6 text-center">No saved articles.</p>
          ) : openArticle ? (
            <div>
              <Button variant="ghost" className="rounded-none mb-3 font-mono text-xs" onClick={() => setOpenArticle(null)}>
                <ArrowLeft className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Back to list
              </Button>
              <h3 className="font-heading font-bold text-lg mb-2">{openArticle.title}</h3>
              {openArticle.image_data_url && <img src={openArticle.image_data_url} alt="" className="w-full border border-border mb-3" />}
              <div className="prose prose-sm max-w-none whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">{openArticle.article}</div>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {viewSaved.map((art) => (
                <button key={art.id} data-testid="admin-saved-item" onClick={() => setOpenArticle(art)} className="w-full text-left py-3 hover:bg-secondary px-2 transition-colors">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono text-[10px] uppercase text-muted-foreground">{art.language}</span>
                    {art.eeat && <span className="font-mono text-[10px] bg-[#111110] text-[#ffce00] px-1.5">{art.eeat.overall_pct}%</span>}
                  </div>
                  <p className="font-heading font-semibold text-sm line-clamp-2">{art.title}</p>
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
